import { LitElement, PropertyValues, TemplateResult } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { WEB_COMPONENT_GZ_GENERIC_WIDGET_CONTENT } from '../../../webcomponents.constants';
import { html } from 'lit/static-html.js';
import { debounce } from '../../../utils/event.utils';
import { WidgetEvents, WidgetStateFrontEnd } from '../types';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { translateFn } from '../../../utils/translator.helper';
import { isEmpty } from 'lodash';
import { HealthDashboardInfoStateData } from '@nirvana/shared/dtos/types/widgets/common';
import { InfoBoxSeverity } from '../../../ui/gzInfoBox/types';

/**
 * @summary Component that can be used in order to create widgets.
 * @tag gz-generic-widget
 */
@customElement(WEB_COMPONENT_GZ_GENERIC_WIDGET_CONTENT)
export abstract class GzGenericWidgetContent extends LitElement {
  @property({ type: Object, attribute: true, reflect: true })
  payload: any;

  @property({ type: WidgetStateFrontEnd, attribute: true, reflect: true })
  state: WidgetStateFrontEnd = WidgetStateFrontEnd.IS_LOADING;

  @property({ type: Array, attribute: true, reflect: true })
  resizeBreakingPoints: number[] = [];

  @property({ type: Object, attribute: true, reflect: true })
  dependencies;

  @property({ type: Number, attribute: true, reflect: true })
  loadingRows = 1;

  @property({ type: Number, attribute: true, reflect: true })
  loadingColumns = 1;

  @query('#content-wrapper')
  private wrapper: HTMLElement;

  @state()
  protected currentWidthInterval: [number, number] = [0, 0];

  @state()
  protected contentWidth: number;

  @state()
  protected contentHeight: number;

  protected resizeObserver: ResizeObserver;

  protected strings: any;

  updated(_changedProperties: PropertyValues): void {
    super.updated(_changedProperties);
    if (_changedProperties.has('state') && this.state === WidgetStateFrontEnd.IS_ACTIVE) {
      this.computeDimensions();
    }
  }

  firstUpdated(): void {
    this.resizeObserver = new ResizeObserver(debounce(this.handleResize.bind(this), 300));
    if (this.parentElement) this.resizeObserver.observe(this.parentElement);
  }

  public injectDependencies(dependencies: any): void {
    //implement in child class that needs dependencies
  }

  willUpdate(properties: PropertyValues): void {
    if (!this.state) {
      this.state = WidgetStateFrontEnd.IS_LOADING;
    }
    if (!this.payload && properties.has('payload')) {
      this.payload = properties.get('payload');
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.dispatchEvent(new CustomEvent(WidgetEvents.LOADED_COMPONENT, { bubbles: true, composed: true }));
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.parentElement) this.resizeObserver?.unobserve(this.parentElement);
  }

  render(): TemplateResult {
    let content: TemplateResult;

    // if widget is loading or widget size or state is not yet computed, display loader, otherwise display content
    if (this.state === WidgetStateFrontEnd.IS_LOADING || this.currentWidthInterval[1] === 0 || !this.state) {
      content = this.renderLoader();
    } else {
      content = this.renderContent();

      this.dispatchEvent(
        new CustomEvent(WidgetEvents.UPDATED, {
          bubbles: true,
          composed: true,
          detail: { widgetId: this.tagName.toLowerCase() },
        })
      );
    }

    const style = 'height: 100%';
    return html`<div id="content-wrapper" style=${style}>${content}</div>`;
  }

  protected handleResize(): void {
    this.computeDimensions();
    const widgetPadding = 24;
    const widgetWidth = this.wrapper.offsetWidth + widgetPadding * 2;
    // check if component passed the current breaking points
    if (
      (!isEmpty(this.currentWidthInterval) && widgetWidth < this.currentWidthInterval[0]) ||
      this.currentWidthInterval[1] < widgetWidth
    ) {
      const allBreakingPoints = [0, ...(this.resizeBreakingPoints ?? []), Infinity];
      const index = allBreakingPoints.findIndex(breakingPoint => widgetWidth <= breakingPoint);
      this.currentWidthInterval = [allBreakingPoints[index - 1], allBreakingPoints[index]];
    }
  }

  public handleTitleClick(): void {}

  private computeDimensions(): void {
    if (!this.parentElement) {
      return;
    }
    const verticalPadding = 24 + 16; // 24px top, 16px bottom
    const horizontalPadding = 24 + 24; // 24px left, 24px right
    const titleHeight = 32;
    this.contentWidth = this.parentElement.offsetWidth - horizontalPadding;
    this.contentHeight = this.parentElement.offsetHeight - verticalPadding - titleHeight;
  }

  protected getWidgetHeaderHeight(element: HTMLElement): number {
    const { verticalPadding } = this.getPaddingConstants();
    const widgetContentMargin = getComputedStyle(element, null).getPropertyValue('margin-top').replace('px', '');
    const contentOffset = this.state === WidgetStateFrontEnd.IS_INFO ? 0 : parseInt(widgetContentMargin, 10);
    const widgetTitleHeight = this.offsetTop - verticalPadding - contentOffset;

    return widgetTitleHeight;
  }

  protected getPaddingConstants(
    verticalPadding: number = 0,
    horizontalPadding: number = 0
  ): { verticalPadding: number; horizontalPadding: number } {
    const paddings = getComputedStyle(this).getPropertyValue('--vertical-widget-main-container-padding');
    if (paddings) {
      verticalPadding = parseInt(paddings.split(' ')[0], 10) + 2;
      horizontalPadding = parseInt(paddings.split(' ')[1], 10);
    }
    return { verticalPadding, horizontalPadding };
  }

  protected hasResizeLimits(): boolean {
    return isEmpty(this.currentWidthInterval) || isEmpty(this.resizeBreakingPoints);
  }

  protected hasInheritedLicense(): boolean {
    return this.payload ? (this.payload as HealthDashboardInfoStateData).hasInheritedLicense : false;
  }

  protected renderNoLicenseInfoBox(widgetText: string): TemplateResult | undefined {
    return this.state === WidgetStateFrontEnd.IS_INFO
      ? html`<gz-info-box severity="${InfoBoxSeverity.INFORMATIVE}" class="widget-info-box">
          ${translateFn('No information available!')}
          <br />
          <br />
          ${unsafeHTML(widgetText)}
        </gz-info-box>`
      : undefined;
  }

  protected renderChangedInfoBox(text?: string): TemplateResult | undefined {
    return text && this.state === WidgetStateFrontEnd.IS_CHANGED
      ? html`<gz-info-box severity="${InfoBoxSeverity.UPDATE}" class="widget-info-box">
          ${unsafeHTML(text)}
        </gz-info-box>`
      : undefined;
  }

  protected renderLoader(): TemplateResult {
    return html`<gz-loader
      rows="${this.loadingRows}"
      columns="${this.loadingColumns}"
      style="margin-top: calc(var(--spacer) * 2)"
    ></gz-loader>`;
  }

  protected abstract renderContent(): TemplateResult;
}
